import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {AuthService} from 'app/core/services/auth.service';
import {map} from 'rxjs/operators';
import {AppICanFetcher} from 'app/core/services/current-app/fetcher';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {findParam} from 'app/core/helpers/routing';
import {AppsICanManage_appsICanManage, AppsICanManage_appsICanManage_nodes} from 'app/core/services/current-app/gql/AppsICanManage';
import {LocalStorageService} from '../local-storage.service';
import {app_app} from 'app/features/apps/services/gql/app';
import {AppPermissions} from 'app/features/apps/interfaces/app.interface';

export interface AppInfo {
    id: string;
    name?: string;
    platform?: string;
    permissions?: AppPermissions;
}

@Injectable({
    providedIn: 'root',
})
export class SelectedAppService {
    currentState$ = new BehaviorSubject<{
        app: AppInfo;
        user: string;
    }>({
        app: this.buildAppInfo(''),
        user: '',
    });
    selectedUser$ = this.currentState$.pipe(map(v => v.user));
    selectedApp$ = this.currentState$.pipe(map(v => v.app));

    currentAppModel: BehaviorSubject<app_app> = new BehaviorSubject<app_app>(null);

    listUserApps$: BehaviorSubject<AppsICanManage_appsICanManage_nodes[]> = new BehaviorSubject<AppsICanManage_appsICanManage_nodes[]>([]);

    AppsICanManage$: BehaviorSubject<AppsICanManage_appsICanManage> = new BehaviorSubject<AppsICanManage_appsICanManage>(null);
    AppsICanManageObs$: Observable<AppsICanManage_appsICanManage> = this.AppsICanManage$.asObservable();

    get selectedApp(): string {
        return this.currentState$.getValue().app.id;
    }

    get selectedAppInfo(): AppInfo {
        return this.currentState$.getValue().app;
    }

    get selectedUser(): string {
        return this.currentState$.getValue().user;
    }

    fetcher: AppICanFetcher;

    get appModel(): app_app {
        return this.currentAppModel.getValue();
    }

    get appsManageList(): AppsICanManage_appsICanManage {
        return this.AppsICanManage$.getValue();
    }

    constructor(
        private localStorage: LocalStorageService,
        private auth: AuthService,
        public router: Router,
        public activatedRoute: ActivatedRoute,
        api: ApiService
    ) {
        this.fetcher = new AppICanFetcher(api);
    }

    buildAppInfo(app: any) {
        return Object.keys(app).length === 0
            ? {id: '', name: '', platform: ''}
            : {
                  id: app.id,
                  name: 'name' in app && app.name ? app.name : app.label,
                  platform: app.platform,
                  permissions: app.permissions,
              };
    }

    async setSelectUser(userId: string) {
        let appsICanManage = await this.fetcher.loadAppsICanManage(userId);
        this.listUserApps$.next(appsICanManage.nodes);
        const next = {...this.currentState$.getValue()};
        next.user = userId;
        this.currentState$.next(next);
    }

    async setSelectApp(appId: string) {
        const currentApp = await this.findCurrentApp(appId);
        const next = {...this.currentState$.getValue()};
        next.app = this.buildAppInfo(currentApp);
        this.currentState$.next(next);
    }

    async findCurrentApp(appId: string): Promise<AppInfo> {
        const list = this.listUserApps$.getValue();
        if (!list.length) {
            if (appId === this.selectedApp) {
                return this.buildAppInfo(this.appModel);
            } else {
                return await this.fetcher.fetchApp(appId).then(x => this.buildAppInfo(x));
            }
        } else {
            let appManage = list.find(app => app.id === appId) ?? '';
            return this.buildAppInfo(appManage);
        }
    }

    saveSelectedToLocalStorage(name: string, id: string) {
        if (this.localStorage.getItem(name) !== id && id) {
            this.localStorage.setItem(name, id);
        }
    }

    private async resolveNewState(app: string, user: string): Promise<{app: AppInfo; user: string}> {
        const currentAppModel: app_app = this.appModel;

        if (user) {
            if (this.appsManageList && !this.appsManageList.appOwners.find(owner => owner.id === user)) {
                this.fetcher.loadAppsICanManage(user).then(apps => {
                    this.AppsICanManage$.next(apps);
                });
            }

            this.saveSelectedToLocalStorage('user_id', user);
            if (currentAppModel) {
                return {app: this.buildAppInfo(currentAppModel), user};
            }
        }

        if (app) {
            const owner = currentAppModel ? currentAppModel.ownerId : this.auth.currentUserId;
            const id = currentAppModel ? currentAppModel.id : app;
            this.saveSelectedToLocalStorage('user_id', owner);
            this.saveSelectedToLocalStorage('app_id', id);
            return {app: this.buildAppInfo(currentAppModel ? currentAppModel : this.selectedAppInfo), user: owner};
        }

        this.resetSelectedAppInfo();
        return {app: this.buildAppInfo(''), user};
    }

    resetSelectedAppInfo() {
        this.localStorage.removeItem('user_id');
        this.localStorage.removeItem('app_id');
    }

    async extractFromRouter(route: ActivatedRouteSnapshot) {
        let checkApp = this.appModel && this.selectedApp !== this.appModel.id;
        let checkUser = Boolean(this.appModel !== null);

        let app = checkApp ? this.appModel.id : this.selectedApp;
        let user = checkUser ? this.appModel.ownerId : this.auth.currentUserId;

        // try to get updates from queries
        if (route && Object.keys(route.queryParams).length > 0) {
            if (route.queryParams['app_id']) {
                app = route.queryParams['app_id'];
            }
            if (route.queryParams['user_id']) {
                user = route.queryParams['user_id'];
            }
        }
        const appFromParam = findParam(route, 'appId');
        if (appFromParam) {
            app = appFromParam;
        }

        if (!this.auth.getCurrentUserLastValue().permissions.canViewAdminPanel) {
            user = this.auth.getCurrentUserLastValue().id;
        }

        let next = await this.resolveNewState(app, user);

        // nothing was selected yet | show default state
        if (!next.user && !next.app.id) {
            next = await this.resolveNewState(app, this.auth.getCurrentUserLastValue().id);
        }

        this.saveSelectedToLocalStorage('user_id', next.user);
        this.saveSelectedToLocalStorage('app_id', next.app.id);

        this.currentState$.next(next);
    }
}
